import { upsertIntegration, type Integration } from "@/api/integration"
import { Form } from "@/components/form/Form.tsx"
import { useFormState } from "@/components/form/form_state.tsx"
import { Input, PasswordInput } from "@/components/form/Input.tsx"
import { Button } from "@/components/ui/button.tsx"
import type { ComponentChildren } from "preact"
import { useState } from "preact/compat"

/** The username a config-only integration row carries; not a portal login. */
export const PLACEHOLDER_USERNAME = "default"

/** The portal login already saved on an integration, if any. */
export function saved_portal_username(username: string | null | undefined): string | null {
  const trimmed = (username ?? "").trim()
  return trimmed && trimmed !== PLACEHOLDER_USERNAME ? trimmed : null
}

interface PortalLoginSectionProps {
  /** `integration.name` the login is saved on, e.g. `heb`. */
  integration_name: string
  /** How the portal is named to the brand, e.g. "H-E-B portal". */
  portal_label: string
  /** `integration.username` of the row, when the org has one. */
  username?: string | null
  username_label?: string
  onSaved: () => void
  className?: string
  /** What saving the login does, shown above the fields. */
  children?: ComponentChildren
  /** More fields for the same row, shown below the password and saved by `after_save`. */
  extra_fields?: ComponentChildren
  /** Runs once the login is saved, with the saved row; returns an error to show, or `null`. */
  after_save?: (integration: Integration) => Promise<string | null>
}

/**
 * Save a brand's login for a customer's portal on that customer's integration row. Saving it is
 * what schedules the portal sync; the first sync checks the login.
 */
export function PortalLoginSection({
  integration_name,
  portal_label,
  username,
  username_label,
  onSaved,
  className,
  children,
  extra_fields,
  after_save,
}: PortalLoginSectionProps) {
  const saved_username = saved_portal_username(username)
  const [saving, setSaving] = useState(false)
  const form = useFormState({ username: saved_username ?? "", password: "" })

  async function onSubmit() {
    setSaving(true)
    form.set_form_errors([])
    const res = await upsertIntegration(integration_name, form.username.value, { password: form.password.value })
    if (!res.ok) {
      setSaving(false)
      form.set_form_errors([res.error.message || `Could not save the ${portal_label} login`])
      return
    }
    const after_error = after_save ? await after_save(res.value.data) : null
    setSaving(false)
    if (after_error) {
      form.set_form_errors([after_error])
      return
    }
    form.set_form_data({ password: "" })
    form.set_form_success(`${portal_label} login saved.`)
    onSaved()
  }

  return (
    <Form class={`space-y-4 ${className ?? ""}`} onSubmit={onSubmit} alerts={form.form_alerts}>
      {children}
      {saved_username ? (
        <p class="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          A login is saved for <strong>{saved_username}</strong>. Enter it again to change the password.
        </p>
      ) : null}
      <Input
        label={username_label ?? "Portal email or username"}
        type="text"
        name={`${integration_name}_username`}
        required={true}
        bind={form.username}
        noAutofill
      />
      <PasswordInput label="Portal password" required={true} bind={form.password} noAutofill />
      {extra_fields}
      <div class="flex justify-end">
        <Button type="submit" variant="outline" disabled={saving}>
          {saving ? "Saving…" : saved_username ? "Update login" : "Save login"}
        </Button>
      </div>
    </Form>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("saved_portal_username", () => {
    it("treats the config-only placeholder as no login", () => {
      expect(saved_portal_username("default")).toBeNull()
      expect(saved_portal_username("")).toBeNull()
      expect(saved_portal_username(undefined)).toBeNull()
      expect(saved_portal_username(" ar@brand.com ")).toBe("ar@brand.com")
    })
  })
}
